import * as tslib_1 from "tslib";
import { combine, extend, getGUID, mergeHeaders, mergeOptions, objectDefinedNotNull, hOP, getHashCode, stringIsNullOrEmpty, } from "@pnp/common";
import { CachingOptions, Queryable } from "@pnp/odata";
import { SPHttpClient, toAbsoluteUrl } from "@pnp/sp";
import { ObjectPathQueue } from "./objectpath";
import { methodAction, objectPath, objectProperties, opQuery } from "./opactionbuilders";
import { method, property } from "./opbuilders";
import { ProcessQueryParser } from "./parsers";
var ProcessQueryPath = "_vti_bin/client.svc/ProcessQuery";
var ClientSvcQueryable = /** @class */ (function (_super) {
    tslib_1.__extends(ClientSvcQueryable, _super);
    function ClientSvcQueryable(parent, _objectPaths) {
        if (parent === void 0) { parent = ""; }
        if (_objectPaths === void 0) { _objectPaths = null; }
        var _this = _super.call(this) || this;
        _this._objectPaths = _objectPaths;
        _this._selects = [];
        _this._batch = null;
        _this._batchDependency = null;
        if (typeof parent === "string") {
            // we assume the parent here is an absolute url to a web
            _this._parentUrl = parent;
            _this._url = combine(parent.replace(ProcessQueryPath, ""), ProcessQueryPath);
            if (!objectDefinedNotNull(_this._objectPaths)) {
                _this._objectPaths = new ObjectPathQueue();
            }
        }
        else {
            _this._parentUrl = parent._parentUrl;
            _this._url = combine(parent._parentUrl, ProcessQueryPath);
            if (!objectDefinedNotNull(_objectPaths)) {
                _this._objectPaths = parent._objectPaths.clone();
            }
            _this.configureFrom(parent);
        }
        return _this;
    }
    /**
     * Choose which fields to return
     *
     * @param selects One or more fields to return
     */
    ClientSvcQueryable.prototype.select = function () {
        var selects = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            selects[_i] = arguments[_i];
        }
        [].push.apply(this._selects, selects);
        return this;
    };
    /**
     * Adds this query to the supplied batch
     *
     */
    ClientSvcQueryable.prototype.inBatch = function (batch) {
        if (this.batch !== null) {
            throw Error("This query is already part of a batch.");
        }
        if (objectDefinedNotNull(batch)) {
            this._batch = batch;
            this._batchDependency = batch.addDependency();
        }
        return this;
    };
    /**
     * Gets the full url with query information
     *
     */
    ClientSvcQueryable.prototype.toUrlAndQuery = function () {
        return _super.prototype.toUrl.call(this) + "?" + Array.from(this.query).map(function (v) { return v[0] + "=" + v[1]; }).join("&");
    };
    ClientSvcQueryable.prototype.getSelects = function () {
        return objectDefinedNotNull(this._selects) ? this._selects : [];
    };
    /**
     * Gets a child object based on this instance's paths and the supplied paramters
     *
     * @param factory Instance factory of the child type
     * @param methodName Name of the method used to load the child
     * @param params Parameters required by the method to load the child
     */
    ClientSvcQueryable.prototype.getChild = function (factory, methodName, params) {
        var objectPaths = this._objectPaths.copy();
        objectPaths.add(method(methodName, params, 
        // actions
        objectPath()));
        return new factory(this, objectPaths);
    };
    /**
     * Gets a property of the current instance
     *
     * @param factory Instance factory of the child type
     * @param propertyName Name of the property to load
     */
    ClientSvcQueryable.prototype.getChildProperty = function (factory, propertyName) {
        var objectPaths = this._objectPaths.copy();
        objectPaths.add(property(propertyName));
        return new factory(this, objectPaths);
    };
    /**
     * Sends a request
     *
     * @param op
     * @param options
     * @param parser
     */
    ClientSvcQueryable.prototype.send = function (objectPaths, options, parser) {
        if (options === void 0) { options = {}; }
        if (parser === void 0) { parser = null; }
        // here we need to create a clone because all the string indexes and references
        // will be updated and all need to relate for this operation being sent. The parser
        // and the postCore method need to share an independent value of the objectPaths
        // See for https://github.com/pnp/pnpjs/issues/419 for details
        var clonedOps = objectPaths.clone();
        if (!objectDefinedNotNull(parser)) {
            // we assume here that we want to return for this index path
            parser = new ProcessQueryParser(clonedOps.last);
        }
        if (this.hasBatch) {
            // this is using the options variable to pass some extra information downstream to the batch
            options = extend(options, {
                clientsvc_ObjectPaths: clonedOps,
            });
        }
        else {
            if (!hOP(options, "body")) {
                options = extend(options, {
                    body: clonedOps.toBody(),
                });
            }
        }
        return _super.prototype.postCore.call(this, options, parser);
    };
    /**
     * Sends the request, merging the result data with a new instance of factory
     */
    ClientSvcQueryable.prototype.sendGet = function (factory) {
        var _this = this;
        var ops = this._objectPaths.copy().appendActionToLast(opQuery(this.getSelects()));
        return this.send(ops).then(function (r) { return extend(new factory(_this), r); });
    };
    /**
     * Sends the request, merging the result data array with a new instances of factory
     */
    ClientSvcQueryable.prototype.sendGetCollection = function (factory) {
        var ops = this._objectPaths.copy().appendActionToLast(opQuery([], this.getSelects()));
        return this.send(ops).then(function (r) { return r.map(function (d) { return extend(factory(d), d); }); });
    };
    /**
     * Invokes the specified method on the server and returns the result
     *
     * @param methodName Name of the method to invoke
     * @param params Method parameters
     * @param actions Any additional actions to execute in addition to the method invocation (set property for example)
     */
    ClientSvcQueryable.prototype.invokeMethod = function (methodName, params) {
        if (params === void 0) { params = null; }
        var actions = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            actions[_i - 2] = arguments[_i];
        }
        return this.invokeMethodImpl(methodName, params, actions, opQuery([], null));
    };
    /**
     * Invokes a method action that returns a single result and does not have an associated query (ex: GetDescription on Term)
     *
     * @param methodName Name of the method to invoke
     * @param params Method parameters
     * @param actions Any additional actions to execute in addition to the method invocation (set property for example)
     */
    ClientSvcQueryable.prototype.invokeMethodAction = function (methodName, params) {
        if (params === void 0) { params = null; }
        var actions = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            actions[_i - 2] = arguments[_i];
        }
        return this.invokeMethodImpl(methodName, params, actions, null, true);
    };
    /**
     * Invokes the specified non-query method on the server
     *
     * @param methodName Name of the method to invoke
     * @param params Method parameters
     * @param actions Any additional actions to execute in addition to the method invocation (set property for example)
     */
    ClientSvcQueryable.prototype.invokeNonQuery = function (methodName, params) {
        if (params === void 0) { params = null; }
        var actions = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            actions[_i - 2] = arguments[_i];
        }
        // by definition we are not returning anything from these calls so we should not be caching the results
        this._useCaching = false;
        return this.invokeMethodImpl(methodName, params, actions, null, true);
    };
    /**
     * Invokes the specified method on the server and returns the resulting collection
     *
     * @param methodName Name of the method to invoke
     * @param params Method parameters
     * @param actions Any additional actions to execute in addition to the method invocation (set property for example)
     */
    ClientSvcQueryable.prototype.invokeMethodCollection = function (methodName, params) {
        if (params === void 0) { params = null; }
        var actions = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            actions[_i - 2] = arguments[_i];
        }
        return this.invokeMethodImpl(methodName, params, actions, opQuery([], []));
    };
    /**
     * Updates this instance, returning a copy merged with the updated data after the update
     *
     * @param properties Plain object of the properties and values to update
     * @param factory Factory method use to create a new instance of FactoryType
     */
    ClientSvcQueryable.prototype.invokeUpdate = function (properties, factory) {
        var _this = this;
        var ops = this._objectPaths.copy();
        // append setting all the properties to this instance
        objectProperties(properties).map(function (a) { return ops.appendActionToLast(a); });
        ops.appendActionToLast(opQuery([], null));
        return this.send(ops).then(function (r) { return extend(new factory(_this), r); });
    };
    /**
     * Converts the current instance to a request context
     *
     * @param verb The request verb
     * @param options The set of supplied request options
     * @param parser The supplied ODataParser instance
     * @param pipeline Optional request processing pipeline
     */
    ClientSvcQueryable.prototype.toRequestContext = function (verb, options, parser, pipeline) {
        var _this = this;
        return toAbsoluteUrl(this.toUrlAndQuery()).then(function (url) {
            mergeOptions(options, _this._options);
            var headers = new Headers();
            mergeHeaders(headers, options.headers);
            mergeHeaders(headers, {
                "accept": "*/*",
                "content-type": "text/xml",
            });
            options = extend(options, { headers: headers });
            // we need to do some special cache handling to ensure we have a good key
            if (_this._useCaching) {
                var keyStr = options.body;
                if (stringIsNullOrEmpty(keyStr)) {
                    if (hOP(options, "clientsvc_ObjectPaths")) {
                        // if we are using caching and batching together we need to create our string from the paths stored for the
                        // batching operation (see: https://github.com/pnp/pnpjs/issues/449) but not update the ones passed to
                        // the batch as they will be indexed during the batch creation process
                        keyStr = options.clientsvc_ObjectPaths.clone().toBody();
                    }
                    else {
                        // this case shouldn't happen
                        keyStr = "";
                    }
                }
                // because all the requests use the same url they would collide in the cache we use a special key
                var cacheKey = "PnPjs.ProcessQueryClient(" + getHashCode(keyStr) + ")";
                if (objectDefinedNotNull(_this._cachingOptions)) {
                    // if our key ends in the ProcessQuery url we overwrite it
                    if (/\/client\.svc\/ProcessQuery\?$/i.test(_this._cachingOptions.key)) {
                        _this._cachingOptions.key = cacheKey;
                    }
                }
                else {
                    _this._cachingOptions = new CachingOptions(cacheKey);
                }
            }
            var dependencyDispose = _this.hasBatch ? _this._batchDependency : function () { return; };
            // build our request context
            var context = {
                batch: _this.batch,
                batchDependency: dependencyDispose,
                cachingOptions: _this._cachingOptions,
                clientFactory: function () { return new SPHttpClient(); },
                isBatched: _this.hasBatch,
                isCached: _this._useCaching,
                options: options,
                parser: parser,
                pipeline: pipeline,
                requestAbsoluteUrl: url,
                requestId: getGUID(),
                verb: verb,
            };
            return context;
        });
    };
    /**
     * Blocks a batch call from occuring, MUST be cleared by calling the returned function
    */
    ClientSvcQueryable.prototype.addBatchDependency = function () {
        if (this._batch !== null) {
            return this._batch.addDependency();
        }
        return function () { return null; };
    };
    Object.defineProperty(ClientSvcQueryable.prototype, "hasBatch", {
        /**
         * Indicates if the current query has a batch associated
         *
         */
        get: function () {
            return objectDefinedNotNull(this._batch);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ClientSvcQueryable.prototype, "batch", {
        /**
         * The batch currently associated with this query or null
         *
         */
        get: function () {
            return this.hasBatch ? this._batch : null;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Executes the actual invoke method call
     *
     * @param methodName Name of the method to invoke
     * @param params Method parameters
     * @param queryAction Specifies the query action to take
     */
    ClientSvcQueryable.prototype.invokeMethodImpl = function (methodName, params, actions, queryAction, isAction) {
        if (isAction === void 0) { isAction = false; }
        var ops = this._objectPaths.copy();
        if (isAction) {
            ops.appendActionToLast(methodAction(methodName, params));
        }
        else {
            ops.add(method.apply(void 0, [methodName, params].concat([objectPath()].concat(actions, [queryAction]))));
        }
        return this.send(ops);
    };
    return ClientSvcQueryable;
}(Queryable));
export { ClientSvcQueryable };
//# sourceMappingURL=clintsvcqueryable.js.map